import {useSpecialtyByIdLazyQuery} from "@components/Booking/Complete/graphql/__generated__/specialty-by-id.type";
import {
  sendApptConfirmedEvent,
  sendApptConfirmedGAEvent,
} from "@components/Booking/SlotPicking/events";
import * as Sentry from "@sentry/nextjs";
import {useLazyMonolithLocationFieldsBySlugQuery} from "@services/graphql/queries/location";
import {bookOrReschedule} from "@services/monolith/appointments/appointments";
import {
  BookAppointmentRequest,
  BookedAppointment,
  RescheduleAppointmentRequest,
} from "@services/monolith/appointments/types";
import {Address} from "@services/types";
import {useRouter} from "next/router";
import {useCallback, useMemo} from "react";
import {EventType} from "src/utils/analytics/constants";
import {queueEvent} from "src/utils/analytics/eventQueue";
import {useGeolocation} from "src/utils/browser-storage/location";
import {HTTPError} from "src/utils/fetch/httpError";

import {ParsedBookingQuery, buildBookingLink, useParsedBookingQuery} from "../bookingQuery";
import {getMilesAway} from "../webBookingAnalytics";
import {usePostBookingProgressMutation} from "../bookingProgress/useBookingProgressMutation";

type RedirectParams = Partial<ParsedBookingQuery>;

/**
 *
 * @param otherQueryParams Any other query params to be added to redirects on top of `useParsedBookingQuery` result. You probably only need this if you are trying to use this with a parsed redirect url.
 * @param dropoutVisitorId This value must be passed in order for abandoned flows to not be repeated.
 *
 * @returns A function that will book an appointment, perform post-booking analytics, and navigate to the confirmation page. If the user has no auth token, the return will be `undefined`. It resolves on completing navigation.
 * This hook will perform a location fetch by slug and fetch specialty by id  on mount (if slug is not cached).
 *
 * Error logging for the booking request is handled by this hook, but the caller should handle the UX on failed booking.
 */
export const useBookAndGoToConfirmation = (
  otherQueryParams: Partial<ParsedBookingQuery> = {},
  dropoutVisitorId?: string,
) => {
  const query = useParsedBookingQuery();
  const specialtyId = query.specialtyId;
  const patchedQuery = useMemo(() => ({...query, ...otherQueryParams}), [otherQueryParams, query]);

  // Only running this to get data for Google analytics, but it should be cached.
  const [fetchLocationBySlug] = useLazyMonolithLocationFieldsBySlugQuery();
  // Only running this to get data for Google analytics, but it is needed in confirmation step anyways.
  const [fetchSpecialtyById] = useSpecialtyByIdLazyQuery();

  const navigateToConfirmation = useNavigateToConfirmation(patchedQuery);
  const navigateToSlotReselection = useNavigateToSlotReselection(patchedQuery);
  const sendConfirmedEvent = useSendConfirmedEvent();

  const [postBookingProgress] = usePostBookingProgressMutation(dropoutVisitorId);

  return useCallback(
    async (
      input: BookAppointmentRequest | RescheduleAppointmentRequest,
      {locationSlug, selectedTime}: RedirectParams,
    ) => {
      const [bookingQuery, locationQuery] = await Promise.all([
        bookOrReschedule(input),
        fetchLocationBySlug({
          variables: {
            slug: locationSlug || "",
          },
        }),
      ]);

      const booking = bookingQuery.booking;
      if (!booking) {
        const bookErr = bookingQuery?.bookingError;
        if (bookErr instanceof HTTPError && bookErr.statusCode === 458) {
          queueEvent({
            typeId: EventType.AppointmentSchedulingFailedSlotUnavailable,
          });

          return navigateToSlotReselection(input.patientId, selectedTime);
        } else {
          Sentry.captureException(bookErr, scope => {
            scope.setContext("Appointment Booking Input", input);
            return scope;
          });
          queueEvent({
            typeId: EventType.AppointmentSchedulingFailed,
          });

          throw bookErr;
        }
      }

      const address = locationQuery.data?.location?.address;
      const practiceId = locationQuery.data?.location?.practiceId;

      const specialtyQuery = await fetchSpecialtyById({
        variables: {
          practiceId: practiceId || "",
          specialtyId: specialtyId || "",
        },
      });
      const specialtyName = specialtyQuery.data?.practice?.specialty?.name;

      postBookingProgress({isBooked: true});
      sendConfirmedEvent(booking, address, "reschedulingApptId" in input);
      sendGoogleAnalyticsEvent(booking.slot, specialtyName, locationSlug || "", input.practiceId);

      await navigateToConfirmation({
        specialtyId: booking.specialtyId,
        selectedPatientId: booking.patientId,
        appointmentId: booking.id,
        selectedTime: booking.slot.time,
        selectedSlotIds: input.slotIds,
        locationSlug: locationSlug || "",
      });

      return booking;
    },
    [
      fetchLocationBySlug,
      fetchSpecialtyById,
      navigateToConfirmation,
      navigateToSlotReselection,
      specialtyId,
      sendConfirmedEvent,
      postBookingProgress,
    ],
  );
};

function sendGoogleAnalyticsEvent(
  slot: unknown,
  specialtyName: string | null = "",
  locationSlug = "",
  practiceId = "",
) {
  sendApptConfirmedGAEvent(
    {
      instant: false,
      virtual: false,
      apptSpecialty: {
        name: specialtyName || "",
      },
      slot,
    },
    locationSlug,
    practiceId,
  );
}

function useSendConfirmedEvent() {
  const userPosition = useGeolocation();
  return useCallback(
    (result: BookedAppointment, address?: Address, rescheduling = false) => {
      sendApptConfirmedEvent(
        {
          apptId: result.id,
          locationId: result.slot.locationId,
          apptReasonId: result.apptReasonId,
          specialtyId: result.specialtyId,
          slotId: result.slot.id,
          milesAway: userPosition && address ? getMilesAway(userPosition, {address}) : undefined,
          timeToAppt: result.slot.time - Date.now(),
        },
        rescheduling,
      );
    },
    [userPosition],
  );
}

function useNavigateToConfirmation(query: Partial<ParsedBookingQuery>) {
  const router = useRouter();
  return useCallback(
    ({
      specialtyId,
      selectedPatientId,
      appointmentId,
      selectedTime,
      selectedSlotIds,
      locationSlug,
    }: Required<
      Pick<
        ParsedBookingQuery,
        | "specialtyId"
        | "selectedPatientId"
        | "appointmentId"
        | "selectedSlotIds"
        | "selectedTime"
        | "locationSlug"
      >
    >) => {
      return router.push(
        buildBookingLink({
          ...query,
          specialtyId,
          selectedPatientId,
          locationSlug,
          selectedTime,
          selectedSlotIds,
          appointmentId,
          isSelectedTimeUnavailable: null,
          doubleBookingOf: null,
        }),
        undefined,
        {shallow: true},
      );
    },
    [query, router],
  );
}

function useNavigateToSlotReselection(query: Partial<ParsedBookingQuery>) {
  const router = useRouter();
  return useCallback(
    (selectedPatientId: string, selectedTime: number | null = 1) => {
      router.push(
        buildBookingLink({
          ...query,
          selectedTime,
          selectedPatientId,
          isSelectedTimeUnavailable: true,
        }),
        undefined,
        {shallow: true},
      );
    },
    [query, router],
  );
}
